import { Fragment, useState } from "react";
import { JOBS_ARRAY } from "../data/ResumeData";
import { Job } from "../types/Types";
import SectionHeader from "./SectionHeader";

function SkillTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11.5px] leading-snug bg-accent-cool/20 border border-accent-cool/60 text-fg hover:bg-accent-cool/35 hover:border-accent-cool transition-colors">
      {children}
    </span>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 text-[13.5px] leading-[1.5] text-fg">
      {items.map((b, i) => (
        <li key={i} className="grid grid-cols-[1.25rem_1fr] gap-1">
          <span aria-hidden="true" className="text-fg-subtle">—</span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

// Group consecutive entries that share a company name
function groupByCompany(jobs: Job[]): Job[][] {
  const groups: Job[][] = [];
  for (const job of jobs) {
    const last = groups[groups.length - 1];
    if (last && last[0].companyName === job.companyName && last[0].location === job.location) {
      last.push(job);
    } else {
      groups.push([job]);
    }
  }
  return groups;
}

// Determine which bullets appear in every role vs. only in some
function analyzeRoles(roles: Job[]) {
  const sharedBullets = roles[0].resumeBullets.filter((b) =>
    roles.every((r) => r.resumeBullets.includes(b)),
  );
  const uniquePerRole = roles.map((r) =>
    r.resumeBullets.filter((b) => !sharedBullets.includes(b)),
  );
  // Combined skill set in original order, deduped
  const seen = new Set<string>();
  const combinedSkills: string[] = [];
  for (const r of roles) {
    for (const s of r.skills) {
      if (!seen.has(s)) {
        seen.add(s);
        combinedSkills.push(s);
      }
    }
  }
  return { sharedBullets, uniquePerRole, combinedSkills };
}

function JobEntry({ job, isOpen, onToggle }: { job: Job; isOpen: boolean; onToggle: () => void }) {
  const isCurrent = job.years.toLowerCase().includes("present");
  const [start, end] = job.years.split(/\s*[-–]\s*/);

  return (
    <article className="border-b border-rule last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left py-3 group"
      >
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[1.0625rem] leading-tight">
              {job.jobName}
              {isCurrent && (
                <span
                  aria-hidden="true"
                  className="inline-block ml-2 align-middle h-1.5 w-1.5 rounded-full bg-accent"
                />
              )}
            </h3>
            <p className="mt-0.5 text-[13px] text-fg-muted">
              {job.companyName}
              <span aria-hidden="true" className="mx-2 text-fg-subtle/60">·</span>
              {job.location}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[12.5px] tabular-nums whitespace-nowrap text-fg-subtle">
              {start}
              {end && (
                <>
                  <span aria-hidden="true" className="mx-1 opacity-60">→</span>
                  <span className={isCurrent ? "text-accent" : ""}>{end}</span>
                </>
              )}
            </span>
            <span
              aria-hidden="true"
              className={`text-[15px] text-fg-subtle group-hover:text-fg transition-all duration-300 ${isOpen ? "rotate-90" : ""}`}
            >
              ›
            </span>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="pb-4 -mt-1">
          <Bullets items={job.resumeBullets} />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {job.skills.map((s) => (
              <SkillTag key={s}>{s}</SkillTag>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function GroupedJobEntry({
  jobs,
  isOpen,
  onToggle,
}: {
  jobs: Job[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  // jobs are in reverse chronological order (newest first)
  const current = jobs[0];
  const earliest = jobs[jobs.length - 1];
  const isCurrent = current.years.toLowerCase().includes("present");
  const [currStart, currEnd] = current.years.split(/\s*[-–]\s*/);
  const tenureStart = earliest.years.split(/\s*[-–]\s*/)[0];

  const { sharedBullets, uniquePerRole, combinedSkills } = analyzeRoles(jobs);

  return (
    <article className="border-b border-rule last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left py-3 group"
      >
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[1.0625rem] leading-tight">
              {current.jobName}
              {isCurrent && (
                <span
                  aria-hidden="true"
                  className="inline-block ml-2 align-middle h-1.5 w-1.5 rounded-full bg-accent"
                />
              )}
            </h3>
            <p className="mt-0.5 text-[13px] text-fg-muted">
              {current.companyName}
              <span aria-hidden="true" className="mx-2 text-fg-subtle/60">·</span>
              {current.location}
            </p>
            <p className="mt-1 text-[12px] text-fg-subtle italic">
              Promoted from {earliest.jobName}
              <span aria-hidden="true" className="mx-1.5 not-italic">·</span>
              {currStart}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[12.5px] tabular-nums whitespace-nowrap text-fg-subtle">
              {tenureStart}
              {currEnd && (
                <>
                  <span aria-hidden="true" className="mx-1 opacity-60">→</span>
                  <span className={isCurrent ? "text-accent" : ""}>{currEnd}</span>
                </>
              )}
            </span>
            <span
              aria-hidden="true"
              className={`text-[15px] text-fg-subtle group-hover:text-fg transition-all duration-300 ${isOpen ? "rotate-90" : ""}`}
            >
              ›
            </span>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="pb-4 -mt-1">
          {/* Role progression strip — chronological, oldest left */}
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px]">
            {jobs
              .slice()
              .reverse()
              .map((j, i, arr) => {
                const [s, e] = j.years.split(/\s*[-–]\s*/);
                const isLatest = i === arr.length - 1;
                return (
                  <Fragment key={`${j.jobName}-${j.years}`}>
                    <div className={isLatest ? "text-fg" : "text-fg-muted"}>
                      <span className="font-display text-[13.5px]">{j.jobName}</span>
                      <span aria-hidden="true" className="mx-1.5 text-fg-subtle">·</span>
                      <span className="tabular-nums text-fg-subtle">
                        {s}
                        {e && (
                          <>
                            <span aria-hidden="true" className="mx-1 opacity-60">→</span>
                            {e}
                          </>
                        )}
                      </span>
                    </div>
                    {i < arr.length - 1 && (
                      <span aria-hidden="true" className="text-fg-subtle">→</span>
                    )}
                  </Fragment>
                );
              })}
          </div>

          {/* Shared bullets */}
          {sharedBullets.length > 0 && <Bullets items={sharedBullets} />}

          {/* Per-role unique bullets, oldest first */}
          {jobs
            .slice()
            .reverse()
            .map((j, i) => {
              // i counts oldest→newest, but uniquePerRole is in original (newest→oldest) order
              const originalIdx = jobs.length - 1 - i;
              const unique = uniquePerRole[originalIdx];
              if (!unique || unique.length === 0) return null;
              return (
                <div key={`${j.jobName}-add`} className="mt-4">
                  <p className="mb-1.5 text-[10.5px] uppercase tracking-[0.16em] text-fg-subtle">
                    Added as {j.jobName}
                  </p>
                  <Bullets items={unique} />
                </div>
              );
            })}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {combinedSkills.map((s) => (
              <SkillTag key={s}>{s}</SkillTag>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function Experience() {
  const groups = groupByCompany(JOBS_ARRAY);
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0]));

  const toggle = (idx: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <section aria-labelledby="experience-heading">
      <SectionHeader title="Work" />
      <h2 id="experience-heading" className="sr-only">Work Experience</h2>
      <div>
        {groups.map((group, i) => {
          const isOpen = openSet.has(i);
          const onToggle = () => toggle(i);
          if (group.length === 1) {
            return (
              <JobEntry
                key={`${group[0].companyName}-${group[0].years}`}
                job={group[0]}
                isOpen={isOpen}
                onToggle={onToggle}
              />
            );
          }
          return (
            <GroupedJobEntry
              key={`${group[0].companyName}-group`}
              jobs={group}
              isOpen={isOpen}
              onToggle={onToggle}
            />
          );
        })}
      </div>
    </section>
  );
}

export default Experience;
